import {
  FileCheck2,
  FileClock,
  FileCog
} from 'lucide-react';

import {
  AccessTimeRounded,
  AssignmentRounded,
  CheckCircleOutlineRounded,
  HeadsetMicRounded,
  PersonRounded,
  ReportProblemRounded
} from '@mui/icons-material';

const MAX_CHART_POINTS = 12;
const EMPTY_CHART_DATA = Array(MAX_CHART_POINTS).fill(0);

export const formatDashboardPercentage = value => {
  const percentage = Number(value);

  return Number.isFinite(percentage)
    ? Number(percentage.toFixed(2))
    : 0;
};

const normalizeChartData = values => {
  const normalizedValues = Array.isArray(values)
    ? values.slice(0, MAX_CHART_POINTS).map(value => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : 0;
    })
    : [];

  return [
    ...normalizedValues,
    ...Array(MAX_CHART_POINTS - normalizedValues.length).fill(0),
  ];
};

const parseTimeToMinutes = value => {
  const [hours, minutes] = String(value || '').split(':').map(Number);

  if (
    !Number.isInteger(hours)
    || !Number.isInteger(minutes)
    || hours < 0
    || hours > 23
    || minutes < 0
    || minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
};

const formatMinutesAsTime = value => {
  const minuteOfDay = ((value % 1440) + 1440) % 1440;
  const hours = Math.floor(minuteOfDay / 60);
  const minutes = minuteOfDay % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const createProjectedTimeBucket = (timeBuckets, index) => {
  const lastBucket = timeBuckets[timeBuckets.length - 1];
  const lastStart = parseTimeToMinutes(lastBucket?.start);
  const lastEnd = parseTimeToMinutes(lastBucket?.end);

  if (lastStart === null || lastEnd === null) {
    return {
      key: `empty-time-bucket-${index}`,
      hour: '',
    };
  }

  const bucketDuration = (lastEnd - lastStart + 1440) % 1440 || 60;
  const projectedIndex = index - timeBuckets.length;
  const startMinute = lastEnd + projectedIndex * bucketDuration;
  const endMinute = startMinute + bucketDuration;
  const start = formatMinutesAsTime(startMinute);
  const end = formatMinutesAsTime(endMinute);

  return {
    key: `${start}-${end}`,
    hour: `${start} - ${end}`,
  };
};

const defaultMetricCards = [
  {
    id: 1,
    key: 'totalTickets',
    keyRedirect: "expired.calleds.total.filter",
    msgRedirect: null,
    title: "dashboard.new.operation.title.totalTickets",
    value: 0,
    percentage: `0% em relação ao último mês`,
    color: '#3478f6',
    background: '#eaf2ff',
    icon: AssignmentRounded,
    chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
  {
    id: 2,
    key: 'createdToday',
    keyRedirect: "today.open.calleds",
    msgRedirect: "Redirecionado para a lista de 'Chamados', filtrando os chamados 'Criados hoje'.",
    title: "dashboard.new.operation.title.createdToday",
    value: 0,
    percentage: '0% do total',
    color: '#8652d9',
    background: '#f1eafe',
    icon: AccessTimeRounded,
    chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
  {
    id: 3,
    key: 'inProgress',
    keyRedirect: "expired.calleds.total.filter",
    msgRedirect: null,
    title: "dashboard.new.operation.title.inProgress",
    value: 0,
    percentage: '0% do total',
    color: '#36a948',
    background: '#eaf8ed',
    icon: HeadsetMicRounded,
    chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
  {
    id: 4,
    key: 'criticalSla',
    title: "dashboard.new.operation.title.criticalSla",
    keyRedirect: "expired.calleds.total.filter",
    value: 0,
    percentage: '0% do total',
    color: '#ef3d46',
    background: '#ffebed',
    icon: ReportProblemRounded,
    chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
  {
    id: 5,
    key: 'unassigned',
    keyRedirect: "calleds.without.assigned",
    msgRedirect: null,
    title: "dashboard.new.operation.title.unassigned",
    value: 0,
    percentage: '0% do total',
    color: '#f59e0b',
    background: '#fff4df',
    icon: PersonRounded,
    chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
  {
    id: 6,
    key: 'closed',
    keyRedirect: "expired.calleds.total.filter",
    msgRedirect: null,
    title: 'dashboard.new.operation.title.closed',
    value: 0,
    percentage: '0% do total',
    color: '#8055d9',
    background: '#f1eafe',
    icon: CheckCircleOutlineRounded,
    chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
];

const defaultStatusData = [
  {
    name: 'Novo',
    value: 0,
    percentage: 0,
    color: '#3478f6',
  },
  {
    name: 'Em andamento',
    value: 0,
    percentage: 0,
    color: '#58ad32',
  },
  {
    name: 'Pendente para análise',
    value: 0,
    percentage: 0,
    color: '#f5a000',
  },
  {
    name: 'Aguardando cliente',
    value: 0,
    percentage: 0,
    color: '#8255d9',
  },
  {
    name: 'Atendimentos críticos',
    value: 0,
    percentage: 0,
    color: '#ef3a3a',
  },
];

const defaultOrganizationData = [
  {
    name: 'Suporte TI',
    value: 0,
    percentage: 0,
    color: '#3478f6',
  },
  {
    name: 'Financeiro',
    value: 0,
    percentage: 0,
    color: '#58ad32',
  },
  {
    name: 'Recursos Humanos',
    value: 0,
    percentage: 0,
    color: '#f5a000',
  },
  {
    name: 'Comercial',
    value: 0,
    percentage: 0,
    color: '#8255d9',
  },
  {
    name: 'Infraestrutura',
    value: 0,
    percentage: 0,
    color: '#ef3a3a',
  },
];

const defaultPerformanceData = [
  {
    id: 1,
    key: 'averageWaitTime',
    title: 'Tempo médio de espera (TME)',
    value: '0m',
    description: 'Tempo até a primeira resposta',
    variation: '0%',
    variationText: 'vs período anterior',
    color: '#4caf50',
    chartData: EMPTY_CHART_DATA,
  },
  {
    id: 2,
    key: 'averageResponseTime',
    title: 'Tempo médio de resposta (TMR)',
    value: '0m',
    description: 'Tempo médio de resposta',
    variation: '0%',
    variationText: 'vs período anterior',
    color: '#4caf50',
    chartData: EMPTY_CHART_DATA,
  },
  {
    id: 3,
    key: 'averageHandlingTime',
    title: 'Tempo médio de atendimento (TMA)',
    value: '0m',
    description: 'Tempo médio de resolução',
    variation: '0%',
    variationText: 'vs período anterior',
    color: '#4caf50',
    chartData: EMPTY_CHART_DATA,
  },
];

const defaultSlaItems = [
  {
    title: 'Chamados conformes',
    value: '0%',
    description: 'Chamados em conformidade com o SLA',
    key: 'compliantTickets',
    color: '#21b66f',
    icon: <FileCheck2 />,
  },
  {
    title: 'Tempo para atendimento cumprido',
    value: '0%',
    description: 'SLA de atendimento cumprido',
    key: 'serviceSlaMet',
    color: '#ef5350',
    icon: <FileCog />,
  },
  {
    title: 'Tempo de resolução cumprido',
    value: '0%',
    description: 'SLA de resolução cumprido',
    key: 'resolutionSlaMet',
    color: '#f59e0b',
    icon: <FileClock />,
  }
];

const defaultTicketsByHour = [
  { hour: '00:00', total: 0 },
  { hour: '02:00', total: 0 },
  { hour: '04:00', total: 0 },
  { hour: '06:00', total: 0 },
  { hour: '08:00', total: 0 },
  { hour: '10:00', total: 0 },
  { hour: '12:00', total: 0 },
  { hour: '14:00', total: 0 },
  { hour: '16:00', total: 0 },
  { hour: '18:00', total: 0 },
  { hour: '20:00', total: 0 },
  { hour: '22:00', total: 0 },
];

export const formatDuration = value => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return value;
  }

  const totalSeconds = Math.max(0, Math.round(value));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }

  if (seconds > 0 || parts.length === 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join(' ');
};

const formatVariation = percent =>
  `${percent > 0 ? '+' : ''}${Number.isFinite(percent) ? percent : 0}%`;

const formatBreakdownData = (items, defaults, fallbackLabel) => {
  if (!Array.isArray(items)) {
    return defaults;
  }

  return items.map((item, index) => {
    const defaultItem = defaults[index % defaults.length];
    const hasKey = item?.key !== undefined && item?.key !== null;

    return {
      key: hasKey ? String(item.key) : `${fallbackLabel}-${index + 1}`,
      name: item?.name
        || (hasKey ? `${fallbackLabel} ${item.key}` : defaultItem.name),
      value: item?.value ?? 0,
      percentage: formatDashboardPercentage(item?.percent),
      color: defaultItem.color,
    };
  });
};

export function formatedStatisticsDashboardData({
  statisticsData = null,
  intl = null,
}) {

  const translate = (id, defaultMessage, values) => intl
    ? intl.formatMessage({ id, defaultMessage }, values)
    : defaultMessage;

  const statusTranslationKeys = [
    'dashboard.new.operation.status.new',
    'dashboard.new.operation.status.inProgress',
    'dashboard.new.operation.status.pendingAnalysis',
    'dashboard.new.operation.status.waitingCustomer',
    'dashboard.new.operation.status.critical',
  ];
  const organizationTranslationKeys = [
    'dashboard.new.operation.organization.itSupport',
    'dashboard.new.operation.organization.finance',
    'dashboard.new.operation.organization.humanResources',
    'dashboard.new.operation.organization.sales',
    'dashboard.new.operation.organization.infrastructure',
  ];
  const localizedMetricCards = defaultMetricCards.map(item => ({
    ...item,
    title: translate(item.title, item.title),
    msgRedirect: item.msgRedirect
      ? translate(
        'dashboard.new.operation.message.redirect.createdToday',
        item.msgRedirect
      )
      : item.msgRedirect,
    percentage: item.key === 'totalTickets'
      ? translate(
        'dashboard.new.operation.comparison.lastMonth',
        '{percentage}% compared to the previous month',
        { percentage: 0 }
      )
      : translate(
        'dashboard.new.operation.percentage.ofTotal',
        '{percentage}% of the total',
        { percentage: 0 }
      ),
  }));
  const localizedStatusData = defaultStatusData.map((item, index) => ({
    ...item,
    name: translate(statusTranslationKeys[index], item.name),
  }));
  const localizedOrganizationData = defaultOrganizationData.map((item, index) => ({
    ...item,
    name: translate(organizationTranslationKeys[index], item.name),
  }));
  const localizedPerformanceData = defaultPerformanceData.map(item => ({
    ...item,
    title: translate(
      `dashboard.new.operation.performance.${item.key}.title`,
      item.title
    ),
    description: translate(
      `dashboard.new.operation.performance.${item.key}.description`,
      item.description
    ),
    variationText: translate(
      'dashboard.new.operation.performance.previousPeriod',
      item.variationText
    ),
  }));
  const localizedSlaItems = defaultSlaItems.map(item => ({
    ...item,
    title: translate(
      `dashboard.new.operation.sla.${item.key}.title`,
      item.title
    ),
    description: translate(
      `dashboard.new.operation.sla.${item.key}.description`,
      item.description
    ),
  }));

  const statisticsByKey = new Map(
    Object.entries(statisticsData || {}).map(([key, value]) => [
      key.toLowerCase(),
      value,
    ])
  );
  const getStatisticsValue = key => statisticsByKey.get(key.toLowerCase());

  //METRICAS CARDS
  const metricCardsData = getStatisticsValue('metricCards') || [];
  const hasMetricKeys = metricCardsData.some(item => item?.key);
  const metricCards = localizedMetricCards.map((defaultItem, index) => {
    const item = hasMetricKeys
      ? metricCardsData.find(({ key }) => key === defaultItem.key)
      : metricCardsData[index];

    if (!item) {
      return defaultItem;
    }

    return {
      ...defaultItem,
      value: item.value,
      percentage: defaultItem.key === 'totalTickets'
        ? translate(
          'dashboard.new.operation.comparison.lastMonth',
          '{percentage}% compared to the previous month',
          { percentage: item.percent }
        )
        : translate(
          'dashboard.new.operation.percentage.ofTotal',
          '{percentage}% of the total',
          { percentage: item.percent }
        ),
      chartData: normalizeChartData(item.values),
    };
  });

  // CHAMADOS POR STATUS
  const statusData = formatBreakdownData(
    getStatisticsValue('statusData'),
    localizedStatusData,
    translate('dashboard.new.operation.status.fallback', 'Status')
  );

  const totalTickets = getStatisticsValue('totalTickets')
    ?? statusData.reduce((total, item) => total + item.value, 0);

  // CHAMAODS POR ORGANIZAÇÃO
  const organizationData = formatBreakdownData(
    getStatisticsValue('organizationData'),
    localizedOrganizationData,
    translate('dashboard.new.operation.organization.fallback', 'Organization')
  );
  const totalOrganizations = getStatisticsValue('totalOrganizations')
    ?? getStatisticsValue('organizationData')?.length
    ?? 0;

  // PERFORMANCE
  const performanceItems = getStatisticsValue('performanceData') || [];
  const hasPerformanceKeys = performanceItems.some(item => item?.key);
  const performanceData = localizedPerformanceData.map((defaultItem, index) => {
    const item = hasPerformanceKeys
      ? performanceItems.find(performanceItem =>
        String(performanceItem?.key).toLowerCase()
        === defaultItem.key.toLowerCase()
      )
      : performanceItems[index];

    if (!item) {
      return {
        ...defaultItem,
        color: '#9ca3af',
      };
    }

    const performancePercent = Number(item.percent);
    const performanceValue = Number(item.value);
    const chartData = normalizeChartData(item.values);
    const hasPerformanceData = (
      Number.isFinite(performanceValue) && performanceValue !== 0
    ) || chartData.some(value => value !== 0);

    return {
      ...defaultItem,
      value: formatDuration(item.value),
      variation: formatVariation(item.percent),
      color: !hasPerformanceData
        ? '#9ca3af'
        : (performancePercent < 0 ? '#ef5350' : '#4caf50'),
      chartData,
    };
  });

  // SLA
  const slaData = getStatisticsValue('slaItems') || [];
  const slaItems = localizedSlaItems.map(defaultItem => {
    const item = slaData.find(
      slaItem => slaItem.key === defaultItem.key
    );

    return item
      ? { ...defaultItem, value: `${item.value}%` }
      : defaultItem;
  });

  // HORARIOS DE ABERTURAS DE CHAMADOS
  const timeBucketData = getStatisticsValue('timeBuckets');
  const hasDynamicTimeBuckets = Array.isArray(timeBucketData)
    && timeBucketData.length > 0;
  const timeBuckets = hasDynamicTimeBuckets
    ? timeBucketData.slice(0, MAX_CHART_POINTS)
    : [];
  const ticketValues = normalizeChartData(getStatisticsValue('ticketsByHour'));
  const ticketsByHour = hasDynamicTimeBuckets
    ? Array.from({ length: MAX_CHART_POINTS }, (_, index) => {
      const bucket = timeBuckets[index];
      const projectedBucket = bucket
        ? null
        : createProjectedTimeBucket(timeBuckets, index);

      return {
        key: bucket
          ? (bucket.key || `${bucket.start}-${bucket.end}`)
          : projectedBucket.key,
        hour: bucket
          ? `${bucket.start} - ${bucket.end}`
          : projectedBucket.hour,
        total: ticketValues[index],
      };
    })
    : defaultTicketsByHour.map((defaultItem, index) => ({
      ...defaultItem,
      total: ticketValues[index],
    }));

  return {
    metricCards,
    statusData,
    totalTickets,
    performanceData,
    organizationData,
    totalOrganizations,
    slaItems,
    timeBuckets,
    ticketsByHour,
  };
}
